'use strict';

const _ =require('underscore');
const Backbone = require('backbone');
const Comment = require('communication/domain/entity/comment'); // eslint-disable-line no-unused-vars
const PartialPage = require('framework/domain/partial-page/partial-page'); // eslint-disable-line no-unused-vars
const PartialPageCollection = require('framework/domain/partial-page/partial-page-collection');
const PartialPageConfiguration = require('framework/domain/partial-page/partial-page-configuration');
const PartialPagination = require('framework/domain/partial-page/partial-pagination');

class CommentLoader {
    constructor() {
        _.extend(this, Backbone.Events);

        this._queue = 0;
    }

    start() {
        if (this._queue === 0) {
            this.trigger('start');
        }
        this._queue++;
    }

    end() {
        this._queue--;
        if (this._queue <= 0) {
            this.trigger('end');
            this._queue = 0;
        }
    }

    delayedEnd(milliseconds = 100) {
        _.delay(() => {
            this.end();
        }, milliseconds);
    }
}

class CommentPartialPageConfiguration extends PartialPageConfiguration {
    /**
     *
     * @param model {Comment}
     * @return {number | moment.Moment}
     */
    modelToNumber(model) {
        return model.getUpdatedAt().unix();
    }
}

class CommentPartialPageCollection extends PartialPageCollection {

    /**
     *
     * @param models {PartialPage[]}
     * @param options {Object}
     * @param options.project {Project}
     * @param options.key {Key}
     * @param options.repository {CommentRepository}
     * @param options.configuration {CommentPartialPageConfiguration}
     */
    constructor(models, options) {
        super(models, options);

        this._project = options.project;
        this._key = options.key;
        this._repository = options.repository;
        this._inPageFetching = false;
        this._pendingComments = [];
        this._commentLoader = new CommentLoader();

        this.listenTo(this._repository, 'add', (/**Comment*/comment) => {
            // 自身の Collection に関連していて、かつ、返信コメントではない場合、追加する
            if (comment.isRelatedTo(this._key) && !comment.getReplyTo()) {
                if (this._inPageFetching) {
                    this._pendingComments.push(comment);
                } else {
                    this.addItem(comment, {allowIndividualPageInMiddle: false});
                }
            }
        });

        this.listenTo(this._repository, 'add:count', (/**Comment*/comment) => {
            const increment = 1;
            // 自身の Collection に関連していて、かつ、返信コメントではない場合、追加する
            if (comment.isRelatedTo(this._key) && !comment.getReplyTo()) {
                if (!this._inPageFetching) {
                    this.addTotalCount(increment);
                }
            }
        });

        this.listenTo(this._commentLoader, 'start', () => {
            this.trigger('loading:start');
        });
        this.listenTo(this._commentLoader, 'end', () => {
            this.trigger('loading:end');
        });
    }

    _fetchStart() {
        this._inPageFetching = true;
    }

    _fetchFinish() {
        this._inPageFetching = false;
        _.each(this._pendingComments, comment => {
            this.addItem(comment, {allowIndividualPageInMiddle: false});
        });
        this._pendingComments = [];
    }

    /**
     * Commentとその関連先の情報を取得中か否かの状態を管理するオブジェクト
     *
     * @return {CommentLoader}
     */
    getCommentLoader() {
        return this._commentLoader;
    }

    /**
     * keyを指定して、そのKeyを含む部分ページがローカルに存在していれば、それを返す
     *
     * @param key {Key}
     * @return {PartialPage|null}
     */
    getPageByKey(key) {
        const page = _.find(this.models, (/**PartialPage*/partialPage) => {
            const itemCollection = partialPage.itemCollection();
            return _.find(itemCollection.models, (/**Comment*/comment) => {
                if (key.isEqual(comment.key)) {
                    return true;
                }
                const replyComments = comment.getReplyComments();
                return _.find(replyComments.models, c => {
                    return key.isEqual(c.key);
                });
            });
        });

        return page;
    }

    /**
     * cursor, direction, limit を指定して、部分ページを取得する
     *
     * @param cursor {string|null}
     * @param direction {string|null} next or prev
     * @param limit {number|null}
     * @return {Promise<PartialPage||null>}
     * @private
     */
    _fetchPage(cursor = null, direction = null, limit = null) {
        const options = {
            direction: direction
        };

        return new Promise((resolve, reject) => {
            this._repository.fetchByKey(this._key, limit, cursor, options)
                .then(result => {
                    const comments = result.models;
                    const pagination = result.pagination;
                    this.setTotalCount(pagination.totalCount);

                    const resultPartialPage = this.buildPartialPageByItems(
                        comments,
                        new PartialPagination(
                            pagination.prevCursor,
                            pagination.prevMore,
                            pagination.nextCursor,
                            pagination.nextMore
                        )
                    );
                    resolve(resultPartialPage);
                })
                .catch(reject);
        });
    }

    /**
     * Keyを指定して、そのKeyを含む部分ページを取得する
     *
     * @param key {Key}
     * @param limit {number|null}
     * @return {Promise<PartialPage|null>}
     * @private
     */
    _fetchPageByItemKey(key, limit = null) {
        const commentID = key.id;
        return new Promise((resolve, reject) => {
            this._repository.fetchByKeyAndCommentID(this._key, commentID, limit)
                .then(result => {
                    const comments = result.models;
                    const pagination = result.pagination;
                    this.setTotalCount(pagination.totalCount);

                    const resultPartialPage = this.buildPartialPageByItems(
                        comments,
                        new PartialPagination(
                            pagination.prevCursor,
                            pagination.prevMore,
                            pagination.nextCursor,
                            pagination.nextMore
                        )
                    );
                    resolve(resultPartialPage);
                });
        });
    }

    /**
     * 最初の部分ページを取得する
     *
     * @return {Promise<PartialPageCollection>}
     */
    fetchFirstPage() {
        this._fetchStart();

        return new Promise((resolve, reject) => {
            super.fetchFirstPage()
                .then(collection => {
                    this._fetchFinish();
                    resolve(collection);
                })
                .catch(error => {
                    this._fetchFinish();
                    reject(error);
                });
        });
    }

    /**
     * 指定したKeyを含む PartialPage を取得する
     *
     * @param key {Key}
     * @return {Promise<PartialPageCollection>}
     */
    fetchPageByItemKey(key) {
        this._fetchStart();

        return new Promise((resolve, reject) => {
            super.fetchPageByItemKey(key)
                .then(collection => {
                    this._fetchFinish();
                    resolve(collection);
                })
                .catch(error => {
                    this._fetchFinish();
                    reject(error);
                });
        });
    }

    /**
     * 引数に指定された partialPage の次のページを取得する
     *
     * @param partialPage {PartialPage}
     * @return {Promise<PartialPageCollection>}
     */
    fetchNextPage(partialPage) {
        const pagination = partialPage.pagination();
        if (!pagination.nextMore) {
            return new Promise(resolve => {
                resolve(this);
            });
        }

        const cursor = pagination.nextCursor;
        const direction = "next";
        const limit = null;

        this._fetchStart();

        return new Promise((resolve, reject) => {
            this._fetchPage(cursor, direction, limit)
                .then(page => {
                    // partialPage に対して小さい側(過去側)を取得した。取得した page から見ると、大きい側にpartialPageが存在する。
                    page.setBiggerSidePartialPage(partialPage);
                    // 取得したページを collection に追加する
                    this.addPartialPage(page);
                    // partialPage の次のページが取得できたので、次ページ方向の cursor 情報をクリアする
                    partialPage.setPagination(
                        pagination.clearNextSide()
                    );
                    this._fetchFinish();
                    resolve(this);
                })
                .catch(error => {
                    this._fetchFinish();
                    reject(error);
                });
        });
    }

    /**
     * 引数に指定された partialPage の前のページを取得する
     *
     * @param partialPage {PartialPage}
     * @return {Promise<PartialPageCollection>}
     */
    fetchPrevPage(partialPage) {
        const pagination = partialPage.pagination();
        if (!pagination.prevMore) {
            return new Promise(resolve => {
                resolve(this);
            });
        }

        const cursor = pagination.prevCursor;
        const direction = "prev";
        const limit = null;

        this._fetchStart();

        return new Promise((resolve, reject) => {
            this._fetchPage(cursor, direction, limit)
                .then(page => {
                    // partialPage に対して大きい側(現在側)を取得した。取得した page から見ると、小さい側にpartialPageが存在している。
                    page.setSmallSidePartialPage(partialPage);
                    // 取得したページを collection に追加する
                    this.addPartialPage(page);
                    // partialPage の前のページが取得できたので、前ページ方向の cursor 情報をクリアする
                    partialPage.setPagination(
                        pagination.clearPrevSide()
                    );
                    this._fetchFinish();
                    resolve(this);
                })
                .catch(error => {
                    this._fetchFinish();
                    reject(error);
                });
        });
    }

    /**
     * コメント取得のAPI操作を省略して、 0件の Collection を返す
     *
     * @return {Promise<PartialPageCollection>}
     */
    buildEmptyCollection() {
        this.setTotalCount(0);
        return new Promise(resolve => {
            resolve(this);
        })
    }
}

/**
 * CommentPartialPageCollection の新しいオブジェクトを作成します
 *
 * @param options {Object}
 * @param options.key {Key}
 * @param options.repository {CommentRepository}
 * @param options.configuration {CommentPartialPageConfiguration|undefined}
 * @return {CommentPartialPageCollection}
 */
CommentPartialPageCollection.build = (options) => {
    options.configuration = new CommentPartialPageConfiguration();

    return new CommentPartialPageCollection([], options);
};

module.exports = CommentPartialPageCollection;



// WEBPACK FOOTER //
// ./communication/domain/collection/comment-partial-page-collection.js